import { FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Select, { MultiValue } from "react-select";

import { getUserFeatures, UserData } from "@/entities/User";
import { api } from "@/shared/api/api";
import { ReactSelectStyles } from "@/shared/const/reactSelectStyles";
import { AlertOptions, AlertTheme, useAlert } from "@/shared/lib/components/Alert";
import { handleError } from "@/shared/lib/helpers/utils";

import { getPersonalityListData, getPersonalityListIsLoading } from "../../model/selectors/personalitySelectors";

import cls from "./AssignedPersonality.module.scss";

interface Props {
  data: UserData;
  onSuccess?: () => void;
}

type OptionType = { label: string; value: number };

export const AssignedPersonality: FC<Props> = memo(({ data, onSuccess }) => {
  const { showAlert } = useAlert();

  const personalityListIsLoading = useSelector(getPersonalityListIsLoading);
  const personalityListData = useSelector(getPersonalityListData);

  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [selectedOption, setSelectedOption] = useState<MultiValue<OptionType>>([]);

  const userFeatures = getUserFeatures(data);
  const isMultiAdvisorsEnabled = userFeatures.isMultiAdvisors;

  useEffect(() => {
    const options = data.assignedPersonalities.map((personality) => ({
      label: `${personality.firstName} ${personality.lastName}`,
      value: personality.id,
    }));
    setSelectedOption(options);
  }, [data]);

  const onClickEdit = useCallback(() => {
    setDisabled((value) => !value);
  }, []);

  const onChangeList = useCallback(
    async (value: MultiValue<OptionType>) => {
      // limit to 1 selection if multi-advisors is not enabled
      if (!isMultiAdvisorsEnabled && value.length > 1) {
        value = [value[value.length - 1]];
      }

      setSelectedOption(value);

      if (!data) {
        return;
      }

      try {
        await api.post("/api/user-assigned-personalities", {
          user_id: data.id,
          personality_ids: value.map((option) => option.value),
        });

        if (onSuccess) {
          onSuccess();
        }
      } catch (err) {
        const error = handleError(err);

        showAlert({
          theme: AlertTheme.ERROR,
          message: error.details,
        } as AlertOptions);
      }
    },
    [data, onSuccess, showAlert, isMultiAdvisorsEnabled]
  );

  const availableOptions = useMemo(() => {
    if (!personalityListData) {
      return [];
    }

    return personalityListData.map((item) => ({
      label: `${item.firstName} ${item.lastName}`,
      value: item.id,
    }));
  }, [personalityListData]);

  return (
    <div className={cls.AssignedPersonality}>
      <div className={cls.Select}>
        <Select
          isLoading={personalityListIsLoading}
          isDisabled={isDisabled}
          closeMenuOnSelect={!isMultiAdvisorsEnabled}
          styles={ReactSelectStyles}
          value={selectedOption}
          onChange={onChangeList}
          isMulti={true}
          isClearable={false}
          isSearchable={false}
          options={availableOptions}
          components={{ IndicatorSeparator: null }}
        />
      </div>
      <button type="button" className={cls.EditBtn} onClick={onClickEdit} />
    </div>
  );
});
